import { useTranslation } from "react-i18next";
import { Badge } from "@components/Badge/Badge";
import { CirclePlusIcon, DotIcon } from "@components/icons";
import { Tooltip } from "@components/Tooltip/Tooltip";
import {
  RequestedSpBillStateId,
  type IOverviewJointBillService,
  type IRequestedSpBillData,
} from "@services/overview/types";
import {
  PoaSpRequestState,
  type IPoaSpRequest,
} from "@services/powersOfAttorney/types";
import { JointBillTableRowLayout } from "../../../../layouts/JointBillTableRowLayout/JointBillTableRowLayout";
import { ProviderBillNameCell } from "../ProviderBill/components/ProviderBillNameCell/ProviderBillNameCell";
import { RequestedPOABill } from "./components/RequestedPOABill/RequestedPOABill";
import { RequestedSpBill } from "./components/RequestedSpBill/RequestedSpBill";
import { requestedBillStatusBadgeColorMap } from "./types";

interface IRequestedServiceProvidersProps {
  jointBillServiceId: IOverviewJointBillService["jointBillServiceId"];
  periodId: number;
  requestedSpBillData?: IRequestedSpBillData[];
  poaSpRequests?: IPoaSpRequest[];
}

export const RequestedServiceProviders: React.FC<
  IRequestedServiceProvidersProps
> = ({ jointBillServiceId, periodId, requestedSpBillData, poaSpRequests }) => {
  const { t } = useTranslation();

  const statusBadge = (
    <Tooltip content={t(`bill.requestedSp.list.state.description`)}>
      <Badge
        color={requestedBillStatusBadgeColorMap[RequestedSpBillStateId.WAITING]}
        variant="bubble"
        icon={DotIcon}
        iconClassName="size-2"
        size="small"
      >
        {t(`bill.requestedSp.list.state.title`)}
      </Badge>
    </Tooltip>
  );

  return (
    <JointBillTableRowLayout
      nameCell={
        <ProviderBillNameCell
          name={t("bill.requestedSp.list.title")}
          statusBadge={statusBadge}
          providerBadge={
            <div className="flex size-9 items-center justify-center rounded-lg border border-grey-light">
              <CirclePlusIcon className="size-4 text-blue-dark" />
            </div>
          }
        />
      }
      statusCell={statusBadge}
      detailsOpenButtonTitle={t("bill.requestedSp.list.open.title")}
      detailsCloseButtonTitle={t("bill.requestedSp.list.close.title")}
      requestedServiceProviders={
        <div className="divide-y divide-grey-light">
          {requestedSpBillData?.map((requestedBill, index) => (
            <RequestedSpBill
              key={`req-sp-${index}`}
              requestedSpBillData={requestedBill}
              jointBillServiceId={jointBillServiceId}
              periodId={periodId}
            />
          ))}
          {poaSpRequests
            ?.filter(
              (poaRequest) =>
                poaRequest.serviceProviderRequestStateId ===
                PoaSpRequestState.IN_PROGRESS,
            )
            ?.map((requestedPOABill, index) => (
              <RequestedPOABill
                key={`req-poa-${index}`}
                POAServiceProviderRequest={requestedPOABill}
              />
            ))}
        </div>
      }
    />
  );
};
